import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.grass': 'Grass',
    'nav.rock': 'Rock',
    'nav.mulch': 'Mulch',
    'nav.soil': 'Soil',
    'nav.sand': 'Sand',
    'nav.services': 'Services',
    'nav.products': 'Products',
    'nav.blog': 'Blog',
    'nav.about': 'About Us',
    'nav.areas': 'Areas Served',
    'nav.contact': 'Contact',
    
    // Hero Section
    'hero.title': 'Premium Grass & Rocks',
    'hero.subtitle': 'Quality grass, rock, mulch, and soil delivered to your door',
    'hero.cta.quote': 'Get a Quote',
    'hero.cta.call': 'Call (936) 320-0763',
    
    // Company Info
    'company.name': 'The Grass Depot LLC',
    'company.address': '6920 FM 1488 RD, Magnolia, TX 77354',
    'company.phone': '(936) 320-0763',
    
    // Quote Calculator
    'quote.title': 'Calculate Your Quote',
    'quote.product': 'Product',
    'quote.quantity': 'Quantity',
    'quote.unit': 'Unit',
    'quote.miles': 'Distance from our location (miles)',
    'quote.subtotal': 'Subtotal',
    'quote.delivery': 'Delivery Fee',
    'quote.total': 'Total',
    'quote.add': 'Add Item',
    'quote.download': 'Download PDF',
    'quote.email': 'Email Quote',
    
    // Footer
    'footer.serving': 'Serving Montgomery County, TX',
    'footer.hours': 'Business Hours',
    'footer.hours.weekdays': 'Mon-Fri: 7:30 AM - 4:30 PM',
    'footer.hours.saturday': 'Saturday: 8:00 AM - 12:30 PM',
    'footer.hours.sunday': 'Sunday: Closed',
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Phone',
    'contact.form.subject': 'Subject',
    'contact.form.message': 'Message',
    'contact.form.send': 'Send Message',
  },
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.grass': 'Pasto',
    'nav.rock': 'Roca',
    'nav.mulch': 'Mulch',
    'nav.soil': 'Tierra',
    'nav.sand': 'Arena',
    'nav.services': 'Servicios',
    'nav.products': 'Productos',
    'nav.blog': 'Blog',
    'nav.about': 'Nosotros',
    'nav.areas': 'Zonas',
    'nav.contact': 'Contacto',
    
    // Hero Section
    'hero.title': 'Césped y Rocas Premium',
    'hero.subtitle': 'Pasto, roca, mulch y tierra de calidad entregados a su puerta',
    'hero.cta.quote': 'Cotizar',
    'hero.cta.call': 'Llamar (936) 320-0763',
    
    // Company Info
    'company.name': 'The Grass Depot LLC',
    'company.address': '6920 FM 1488 RD, Magnolia, TX 77354',
    'company.phone': '(936) 320-0763',
    
    // Quote Calculator
    'quote.title': 'Calcular Cotización',
    'quote.product': 'Producto',
    'quote.quantity': 'Cantidad',
    'quote.unit': 'Unidad',
    'quote.miles': 'Distancia desde nuestra ubicación (millas)',
    'quote.subtotal': 'Subtotal',
    'quote.delivery': 'Costo de Entrega',
    'quote.total': 'Total',
    'quote.add': 'Agregar Artículo',
    'quote.download': 'Descargar PDF',
    'quote.email': 'Enviar Cotización',
    
    // Footer
    'footer.serving': 'Sirviendo el Condado de Montgomery, TX',
    'footer.hours': 'Horarios',
    'footer.hours.weekdays': 'Lun-Vie: 7:30 AM - 4:30 PM',
    'footer.hours.saturday': 'Sábado: 8:00 AM - 12:30 PM',
    'footer.hours.sunday': 'Domingo: Cerrado',
    
    // Contact
    'contact.title': 'Contáctenos',
    'contact.form.name': 'Nombre',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Teléfono',
    'contact.form.subject': 'Asunto',
    'contact.form.message': 'Mensaje',
    'contact.form.send': 'Enviar Mensaje',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    // Check URL path for language
    const path = window.location.pathname;
    if (path.startsWith('/es')) {
      setLanguage('es');
    } else {
      setLanguage('en');
    }
  }, []);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};